import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Index", key: "index" },
  { to: "/what-we-do", label: "What We Do", key: "wwd" },
  { to: "/about", label: "About", key: "about" },
  { to: "/contact", label: "Contact", key: "contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-bone/85 backdrop-blur-xl border-b border-ink/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" aria-label="Home">
          <Logo size={22} />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l, i) => (
            <NavLink
              key={l.key}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `font-mono-tight text-[11px] link-underline ${
                  isActive ? "text-oxred" : "text-ink"
                }`
              }
            >
              <span className="text-ink/40 mr-2">0{i + 1}</span>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <span className="font-mono-tight text-[10px] text-ink/60">CH · ZUG</span>
          <Link
            to="/contact"
            data-testid="nav-cta-inquire"
            className="group inline-flex items-center gap-2 font-mono-tight text-[11px] text-ink"
          >
            <span className="w-1.5 h-1.5 bg-oxred inline-block" />
            Inquire
          </Link>
        </div>

        <button
          className="md:hidden inline-flex items-center justify-center w-10 h-10"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-bone border-t border-ink/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l, i) => (
                <NavLink
                  key={l.key}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-link-mobile-${l.key}`}
                  className="font-display text-3xl text-ink flex items-baseline gap-4"
                >
                  <span className="font-mono-tight text-xs text-ink/40">0{i + 1}</span>
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
